import * as THREE from 'three';

const SPARKLE_COUNT = 200;

/** Twinkling sparkle points sampled from the brain surface, representing synaptic activity. */
export class SynapseSparkles {
  readonly points: THREE.Points;
  readonly material: THREE.PointsMaterial;

  private readonly geometry: THREE.BufferGeometry;
  private readonly colors: Float32Array;
  private readonly alphas: Float32Array;
  private readonly targetAlphas: Float32Array;

  constructor(brainSurfaceGeometry: THREE.BufferGeometry, glowTexture: THREE.Texture) {
    const surfacePositions = brainSurfaceGeometry.attributes.position;
    const surfaceCount = surfacePositions.count;

    const positions = new Float32Array(SPARKLE_COUNT * 3);
    this.colors = new Float32Array(SPARKLE_COUNT * 3);
    this.alphas = new Float32Array(SPARKLE_COUNT);
    this.targetAlphas = new Float32Array(SPARKLE_COUNT);

    for (let i = 0; i < SPARKLE_COUNT; i++) {
      const vi = Math.floor(Math.random() * surfaceCount);
      const sx = surfacePositions.getX(vi);
      const sy = surfacePositions.getY(vi);
      const sz = surfacePositions.getZ(vi);
      const sr = Math.sqrt(sx * sx + sy * sy + sz * sz);
      const outwardScale = (sr + 0.015) / Math.max(sr, 0.001);

      positions[i * 3] = sx * outwardScale;
      positions[i * 3 + 1] = sy * outwardScale;
      positions[i * 3 + 2] = sz * outwardScale;

      this.alphas[i] = Math.random() * 0.3;
      this.targetAlphas[i] = Math.random() * 0.25;
      this.colors[i * 3] = 0.3;
      this.colors[i * 3 + 1] = 0.8;
      this.colors[i * 3 + 2] = 1.0;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));

    this.material = new THREE.PointsMaterial({
      size: 0.025,
      map: glowTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      // Glow through the opaque cortical shell — see neuralActivityParticles.
      depthTest: false,
      transparent: true,
      opacity: 0.9,
      vertexColors: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  /** activityLevel scales twinkle intensity/opacity (1.0 = baseline resting activity). */
  update(activityLevel: number, tintColor: THREE.ColorRepresentation): void {
    const tint = new THREE.Color(tintColor);

    for (let i = 0; i < SPARKLE_COUNT; i++) {
      this.targetAlphas[i] += (Math.random() - 0.5) * 0.04 * activityLevel;
      this.targetAlphas[i] = Math.max(0.02, Math.min(1.0, this.targetAlphas[i]));
      this.alphas[i] += (this.targetAlphas[i] - this.alphas[i]) * 0.1;

      this.colors[i * 3] = tint.r * this.alphas[i];
      this.colors[i * 3 + 1] = tint.g * this.alphas[i];
      this.colors[i * 3 + 2] = tint.b * this.alphas[i];
    }

    this.geometry.attributes.color.needsUpdate = true;
    this.material.opacity = Math.min(1, 0.5 + activityLevel * 0.1);
  }
}
